/**
 * Telling the open UI that an EXTERNAL agent changed something.
 *
 * The Documents sidebar, the breadcrumb path and the command palette all
 * re-read the tree off one nonce — `documentsUiStore.treeVersion`
 * (`bumpDocumentsTree()`). Nothing polls. Every in-app mutator bumps it:
 * `DocumentsSidebar` for its own CRUD, `DocumentEditorTab` after a title save,
 * `useSyncEngine` / `useTeamBoardsRealtime` when a change arrives from another
 * device, and `useAiExtensionActions` for the in-app assistant's document
 * tools.
 *
 * MCP was the one writer that did not, so a folder or document created by an
 * external client was written correctly, read back correctly over the
 * protocol, and stayed invisible until the page remounted — which is why the
 * owner had to navigate away from Documents and back to see it (2026-09-09).
 *
 * ## Why this is a bump and NOT a navigate — do not "align" it with the AI path
 *
 * `useAiExtensionActions.js:434,449` follows its `bumpDocumentsTree()` with
 * `navigateToPanel('documents.panel')` (and `openDocumentTab`), reasoning that
 * "creating a doc you can't see is pointless". That is right for the in-app
 * assistant: the user just typed the request and is looking at the answer.
 *
 * It is WRONG here. An MCP caller is an external process — Claude Code in a
 * terminal, an editor plugin, a background agent — and the user may not be
 * looking at Apiboo at all. Yanking their window to another panel while they
 * are working is intrusive and surprising, and it is a UI action nobody asked
 * for. **Bump the nonce; never navigate.** `handlers/documents.js` makes the
 * same call for the live-editor branch and is pinned by
 * `mcpDocumentHandlers.test.js` ("19 — ... the window is NEVER navigated").
 *
 * ## Only on success
 *
 * A refused write (capability denial in Rust, `conflict`, `document_too_large`,
 * `not_found`) must not bump: it makes every open view re-read for nothing,
 * and a nonce advancing is a signal that something changed. `runHandler`
 * therefore calls this only on its `ok: true` path.
 *
 * ## Boards — where the bump ALONE is actively wrong
 *
 * Phase 2 adds board writes, and they need one thing documents did not.
 *
 * `TaskBoardTab` persists the OPEN board as a whole-board snapshot on a
 * debounce (`writeBoard` → `extension_records_sync_scope`), which tombstones
 * every record in the scope absent from that array. A record-granular MCP write
 * is not in the tab's in-memory snapshot, so the tab's next persist DELETES it.
 * And `reconcileFromDisk` — the function the boards nonce drives — calls
 * `flushSave()` **first** and re-reads second, by its own comment "so our own
 * writes are never lost to the re-read". So bumping the nonce is not a
 * neutral-but-insufficient step: it is what triggers the flush that destroys
 * the write.
 *
 * Hence the order below, which is normative and is asserted by a test that
 * records store transitions:
 *
 *     PUBLISH  the written records into the open tab's snapshot
 *     BUMP     the nonce, which now re-reads a board that already agrees
 *
 * `publishBoardChanges` → `subscribeBoardChanges` → `patchBoardWithRecords` is
 * the app's own realtime fast path for team boards; MCP reuses it rather than
 * inventing a second mechanism. See `mcp/boardChanges.js`.
 *
 * The bump is also SPLIT BY SURFACE: a board write must not make every open
 * Documents view re-read, and vice versa. `memory_*` stays on documents —
 * agent notes are ordinary documents in a visible folder. The map is pinned
 * against `registry.rs`'s `ToolDef.surface` by the same cross-language scan
 * that pins `MUTATING_TOOLS` against `Access::Write`.
 *
 * ## Deletes refresh, and there is nothing for them to reveal
 *
 * A delete publishes `{ id, deleted: true }` per removed record, which
 * `patchBoardWithRecords`'s delete branch looks up by id in the open board's
 * groups and tasks. The publish-before-bump order matters here for the same
 * reason it matters for a write, in the opposite direction: without it the open
 * tab's snapshot still holds the deleted card, and its next whole-board persist
 * writes that card back — `sync_scope` upserts everything in the array it is
 * given, so an un-patched snapshot RESURRECTS a tombstoned record.
 *
 * A reveal would be meaningless: there is nothing to expand towards, and the
 * board record itself is not paintable (`patchBoardWithRecords` leaves
 * board-level records to the disk re-read). A whole-board delete therefore
 * closes its tab through the bump alone — `reconcileFromDisk`'s `readBoard`
 * returns null and the component closes its own tab.
 *
 * ## Calendar — a bump, and deliberately no navigation
 *
 * `CalendarView` keeps a read-only copy of `listEvents()` in `useState` and
 * re-runs it in an effect keyed on `calendarUiStore.eventsVersion`. There is no
 * effect that writes `events` back, no debounce and no `dirtyRef`, so the
 * boards clobber has no analogue and `bumpCalendarEvents()` alone is correct.
 * Without it an MCP write is written correctly, reads back correctly over the
 * protocol, and stays invisible until the panel remounts — the exact phase-1
 * defect the owner found by hand.
 *
 * `setFocusedDate` and `setView` are the only other things in that store and
 * both are navigations; they are on the no-navigate needle list for that
 * reason.
 *
 * There is no boards equivalent of the documents REVEAL, and that is
 * structural rather than an omission: a board has no collapsed containers to
 * expand (the grid renders every tile, an open board renders every list), so
 * there is nothing a reveal could open. The nearest one-shot slot is a task
 * FOCUS, which opens a modal — a UI action nobody asked for, and one this
 * directory is forbidden from naming.
 */
import { bumpCalendarEvents } from '../extensions/bundled/calendar/calendarUiStore';
import { bumpDocumentsTree } from '../extensions/bundled/documents/documentsUiStore';
import { bumpBoards, publishBoardChanges } from '../extensions/bundled/task-manager/boardsUiStore';
import { requestRevealAfterTool } from './reveal';
import { takeBoardRecords } from './boardChanges';
import { ORIGIN_MCP } from './notify';

/**
 * The tools that change stored records. Derived from — and pinned against —
 * `access: Access::Write` in `src-tauri/src/mcp/registry.rs` by
 * `mcpUiRefresh.test.js`, so this cannot drift as tools are added.
 *
 * The documents six live on the documents tree: memory notes are ordinary
 * documents in a visible folder (O4), so `memory_append` (which may create both
 * the Agent Notes folder and the note) and `memory_purge` (which deletes both)
 * move the same tree the documents tools do. The boards eight move the boards
 * grid and the open board tab instead — see `SURFACE_OF_TOOL`.
 */
export const SURFACE_OF_TOOL = Object.freeze({
  document_create_folder: 'documents',
  document_create: 'documents',
  document_rename: 'documents',
  document_write: 'documents',
  document_move: 'documents',
  document_delete: 'documents',
  memory_append: 'documents',
  memory_purge: 'documents',

  board_create: 'boards',
  board_update: 'boards',
  board_list_create: 'boards',
  board_list_rename: 'boards',
  board_task_create: 'boards',
  board_task_update: 'boards',
  board_task_move: 'boards',
  board_comment_add: 'boards',
  board_task_link_commit: 'boards',
  board_task_unlink_commit: 'boards',
  board_task_delete: 'boards',
  board_list_delete: 'boards',
  board_delete: 'boards',

  appointment_create: 'calendar',
  appointment_update: 'calendar',
  appointment_delete: 'calendar',

  // Phase 5. The FOURTH surface, and the only one whose refresh is a NO-OP —
  // see `refreshUiAfterTool` below and the "Collections" section of this
  // header. It is in this map anyway, and that is load-bearing rather than
  // bookkeeping: `MUTATING_TOOLS` derives from it and is pinned against
  // `Access::Write` in `registry.rs`, so a write tool missing here is red.
  collection_create: 'collections',
  folder_create: 'collections',
  request_create: 'collections',
  spec_import: 'collections',
  // Tranche 3-5. Same surface, same no-op refresh: the store write IS the
  // refresh. The §F2 OPEN-EDITOR patch is NOT done here and must not be moved
  // here — it happens inside `applyCollectionsChange`, in the same step as the
  // store write, so a handler cannot forget it and the two cannot disagree
  // about what landed. See `mcp/requestTabSync.js`.
  request_update: 'collections',
  request_move: 'collections',
  collection_update: 'collections',
  folder_rename: 'collections',
  mock_example_create: 'collections',
  mock_example_delete: 'collections',
  request_delete: 'collections',
  folder_delete: 'collections',
  collection_delete: 'collections',
});

export const MUTATING_TOOLS = Object.freeze(new Set(Object.keys(SURFACE_OF_TOOL)));

/**
 * Called by `runHandler` after a tool SUCCEEDS. A no-op for reads and for any
 * name that is not a known mutator.
 *
 * Two signals, in this order and not the other:
 *
 * 1. **Reveal** (creates only) — park which node the sidebar should expand its
 *    way to. Must be set BEFORE the bump, because the bump is what starts the
 *    sidebar's async re-read and the reveal is consumed once that read
 *    finishes. See `reveal.js`.
 * 2. **Bump** — the re-read itself.
 *
 * The reveal is best-effort and can never turn a successful write into a
 * failure: it reads the tree to work out the ancestor chain, and a store read
 * that fails would otherwise escape into `runHandler`'s catch and be reported
 * to the agent as `write_failed` for a write that landed. Hence the swallow —
 * the bump still happens, so the node still appears, just possibly behind a
 * closed chevron.
 */
export async function refreshUiAfterTool(tool, result) {
  if (!MUTATING_TOOLS.has(tool)) return false;

  if (SURFACE_OF_TOOL[tool] === 'calendar') {
    // A BUMP AND NOTHING ELSE. There is no snapshot to patch (nothing in the
    // calendar writes a whole-scope array back), so boards' publish-then-bump
    // pair collapses to the bump — and there is nothing to reveal either:
    // `calendarUiStore` holds no selected-appointment state at all, so the only
    // movable things are the focused date and the view, and moving either is a
    // NAVIGATION. See `mcp/calendarVisibility.js`, which does the one thing a
    // reveal legitimately can here (un-hide the calendar the write landed in)
    // inside the handler, before the result is built.
    bumpCalendarEvents();
    return true;
  }

  if (SURFACE_OF_TOOL[tool] === 'collections') {
    // NOTHING. The store write in the handler IS the refresh.
    //
    // Every collections subscriber renders off `useCollectionsStore` directly
    // (`App.js:223-234` subscribes to the whole store), so `setCollections` —
    // which is the ONLY way an MCP collections write reaches storage at all,
    // see `mcp/collectionsDb.js` — has already re-rendered the sidebar by the
    // time this runs. There is no `collectionsVersion` nonce anywhere in the
    // app (grepped: `collectionsVersion|bumpCollections|collectionsNonce`,
    // zero hits) and none must be added: inventing a second mechanism beside a
    // working one is exactly what the boards section of this header warns
    // against, and it would fire a re-read of a tree that already agrees.
    //
    // There is no reveal either, and unlike the calendar that IS a real loss:
    // the sidebar has collapsed containers, so a request created inside a
    // collapsed collection renders nowhere. But `expandedCollections` /
    // `expandedFolders` are component `useState` inside `Sidebar.js:871-872`,
    // unreachable from outside — so this surface cannot expand a node and
    // cannot even honestly report whether one is visible. **O36: every create
    // returns `path: {collectionName, folderPath}` and a sentence telling the
    // agent to name it**, which is the honest answer rather than a `visibleNow`
    // we would have to guess at. Lifting that state into a store is a separate
    // task, not an MCP module reaching into a component.
    return true;
  }

  if (SURFACE_OF_TOOL[tool] === 'boards') {
    // PUBLISH BEFORE BUMP. Not a preference: the bump starts
    // `reconcileFromDisk`, whose first action is a flush of the open tab's
    // whole-board snapshot — and that snapshot does not contain this write
    // until the publish has patched it in. Reversing these two lines silently
    // deletes the record that was just written. See the header.
    const staged = takeBoardRecords(result);
    if (staged?.records?.length) {
      try {
        publishBoardChanges(staged.scopeKey, staged.records);
      } catch (err) {
        // Best-effort, exactly like the reveal below: a listener that throws
        // must not turn a landed write into `write_failed`. The bump still
        // happens, so the record still appears — just on the slower path.
        if (process.env.NODE_ENV !== 'test') console.warn('[mcp] publish failed:', tool, err);
      }
    }
    // ORIGIN_MCP: an external subscriber is told about everyone ELSE's writes,
    // never about this one. See `mcp/notify.js`.
    bumpBoards(ORIGIN_MCP);
    return true;
  }

  try {
    await requestRevealAfterTool(tool, result);
  } catch (err) {
    if (process.env.NODE_ENV !== 'test') console.warn('[mcp] reveal failed:', tool, err);
  }
  bumpDocumentsTree(ORIGIN_MCP);
  return true;
}
